import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import StudentManagementPage from './pages/StudentManagementPage';
import PointPage from './pages/PointPage';
import ViewerPage from './pages/ViewerPage';
import AttendancePage from './pages/AttendancePage';
import LoginPage from './pages/LoginPage';
import UsersManagementPage from './pages/UsersManagementPage';
import UserCardPage from './pages/UserCardPage';
import ProfilePage from './pages/ProfilePage';
import GoogleOAuthCallbackPage from './pages/GoogleOAuthCallbackPage';
import StudentCardPage from './pages/StudentCardPage';
import ClassManagementPage from './pages/ClassManagementPage';
import ClassCardPage from './pages/ClassCardPage';
import RegularClassManagementPage from './pages/RegularClassManagementPage';
import SpecialClassManagementPage from './pages/SpecialClassManagementPage';
import SupplementClassManagementPage from './pages/SupplementClassManagementPage';
import SchedulePage from './pages/SchedulePage';
import ScheduleAllPage from './pages/ScheduleAllPage';
import ProtectedRoute from './components/ProtectedRoute';
import { Navigate, useLocation } from "react-router-dom";
import TopNavBar from './components/TopNavBar';
import SideBar from './components/SideBar';

import ClassroomHistoryPage from './pages/ClassroomHistoryPage';
import TeacherHistoryPage from './pages/TeacherHistoryPage';
import StudentsHistoryPage from './pages/StudentsHistoryPage';
import PointsHistoryPage from './pages/PointsHistoryPage';
import CompensationManagementPage from './pages/CompensationManagementPage';
import AttendanceEditPage from './pages/AttendanceEditPage';
import MessagePage from './pages/MessagePage';
import MessageHistoryPage from './pages/MessageHistoryPage';
import TemplatePage from './pages/TemplatePage';
import AlimtalkHistoryPage from './pages/AlimtalkHistoryPage';
import AlimtalkTemplatePage from './pages/AlimtalkTemplatePage';
import ReportPage from './pages/ReportPage';
import PointSettingsPage from './pages/PointSettingsPage';
import PointItemCardPage from './pages/PointItemCardPage';
import BasicSettingsPage from './pages/BasicSettingsPage';
import SettingsAdvancedPage from './pages/SettingsAdvancedPage';
import FaceRegisterPage from './pages/FaceRegisterPage';
import PermissionsManagementPage from './pages/PermissionsManagementPage';

interface DecodedToken {
  username: string;
  sub: number;
  role: "admin" | "teacher" | "viewer";
  iat: number;
  exp: number;
}

function decodeJWT(token: string): DecodedToken | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const decoded = JSON.parse(atob(parts[1]));
    return decoded as DecodedToken;
  } catch (e) {
    return null;
  }
}

function getCategoryFromPath(pathname: string): string {
  if (pathname.startsWith("/settings")) {
    return "settings";
  }
  if (pathname.startsWith("/student") || pathname.startsWith("/history/students")) {
    return "student";
  }
  if (pathname.startsWith("/class") || pathname.startsWith("/schedule")) {
    return "classroom";
  }
  if (pathname.startsWith("/users")) {
    return "settings";
  }
  if (pathname.startsWith("/attendance") || pathname.startsWith("/compensation")) {
    return "attendance";
  }
  if (pathname.startsWith("/message")) {
    return "message";
  }
  if (pathname.startsWith("/point") || pathname.startsWith("/history/points")) {
    return "point";
  }
  return "classroom";
}

function AppContent() {
  const [activeCategory, setActiveCategory] = useState("classroom");
  const location = useLocation();

  useEffect(() => {
    const category = getCategoryFromPath(location.pathname);
    setActiveCategory(category);
  }, [location.pathname]);

  return (
    <>
      <Routes>
        {/* TopNavBar/SideBar 없는 라우트 */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/google/callback" element={<GoogleOAuthCallbackPage />} />
        <Route path="/viewer" element={<ProtectedRoute allow={["viewer"]}><ViewerPage /></ProtectedRoute>} />
        <Route path="/face-register" element={<ProtectedRoute allow={["viewer"]}><FaceRegisterPage /></ProtectedRoute>} />
        
        {/* TopNavBar/SideBar 있는 라우트 */}
        <Route path="*" element={
          <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            <TopNavBar activeCategory={activeCategory} onCategoryChange={setActiveCategory} />
            <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
              <SideBar activeCategory={activeCategory} />
              <main style={{ flex: 1, marginTop: 0, marginLeft: 0, overflowY: "auto", boxSizing: "border-box" }}>
                <Routes>
          <Route path="/student" element={<ProtectedRoute allow={["admin", "teacher"]}><StudentManagementPage /></ProtectedRoute>} />
          <Route path="/student/new" element={<ProtectedRoute allow={["admin", "teacher"]}><StudentCardPage /></ProtectedRoute>} />
          <Route path="/student/:id" element={<ProtectedRoute allow={["admin", "teacher"]}><StudentCardPage /></ProtectedRoute>} />
          <Route path="/class" element={<ProtectedRoute allow={["admin", "teacher"]}><ClassManagementPage /></ProtectedRoute>} />
          <Route path="/class/new" element={<ProtectedRoute allow={["admin", "teacher"]}><ClassCardPage /></ProtectedRoute>} />
          <Route path="/class/regular" element={<ProtectedRoute allow={["admin", "teacher"]}><RegularClassManagementPage /></ProtectedRoute>} />
          <Route path="/class/regular/new" element={<ProtectedRoute allow={["admin", "teacher"]}><ClassCardPage /></ProtectedRoute>} />
          <Route path="/class/regular/:id" element={<ProtectedRoute allow={["admin", "teacher"]}><ClassCardPage /></ProtectedRoute>} />
          <Route path="/class/special" element={<ProtectedRoute allow={["admin", "teacher"]}><SpecialClassManagementPage /></ProtectedRoute>} />
          <Route path="/class/special/new" element={<ProtectedRoute allow={["admin", "teacher"]}><ClassCardPage /></ProtectedRoute>} />
          <Route path="/class/special/:id" element={<ProtectedRoute allow={["admin", "teacher"]}><ClassCardPage /></ProtectedRoute>} />
          <Route path="/class/supplement" element={<ProtectedRoute allow={["admin", "teacher"]}><SupplementClassManagementPage /></ProtectedRoute>} />
          <Route path="/class/supplement/new" element={<ProtectedRoute allow={["admin", "teacher"]}><ClassCardPage /></ProtectedRoute>} />
          <Route path="/class/supplement/:id" element={<ProtectedRoute allow={["admin", "teacher"]}><ClassCardPage /></ProtectedRoute>} />
          <Route path="/class/:id" element={<ProtectedRoute allow={["admin", "teacher"]}><ClassCardPage /></ProtectedRoute>} />
          <Route path="/schedule" element={<ProtectedRoute allow={["admin", "teacher"]}><SchedulePage /></ProtectedRoute>} />
          <Route path="/schedule/all" element={<ProtectedRoute allow={["admin", "teacher"]}><ScheduleAllPage /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute allow={["admin"]}><UsersManagementPage /></ProtectedRoute>} />
          <Route path="/users/new" element={<ProtectedRoute allow={["admin"]}><UserCardPage /></ProtectedRoute>} />
          <Route path="/users/:id" element={<ProtectedRoute allow={["admin"]}><UserCardPage /></ProtectedRoute>} />
          <Route path="/history/students" element={<ProtectedRoute allow={["admin", "teacher"]}><StudentsHistoryPage /></ProtectedRoute>} />
          <Route path="/compensation" element={<ProtectedRoute allow={["admin", "teacher"]}><CompensationManagementPage /></ProtectedRoute>} />
          <Route path="/attendance/edit" element={<ProtectedRoute allow={["admin", "teacher"]}><AttendanceEditPage /></ProtectedRoute>} />
          <Route path="/attendance" element={<ProtectedRoute allow={["admin", "teacher"]}><AttendancePage /></ProtectedRoute>} />
          <Route path="/message" element={<ProtectedRoute allow={["admin", "teacher"]}><MessagePage /></ProtectedRoute>} />
          <Route path="/message/template" element={<ProtectedRoute allow={["admin", "teacher"]}><TemplatePage /></ProtectedRoute>} />
          <Route path="/message/history" element={<ProtectedRoute allow={["admin", "teacher"]}><MessageHistoryPage /></ProtectedRoute>} />
          <Route path="/message/alimtalk-history" element={<ProtectedRoute allow={["admin", "teacher"]}><AlimtalkHistoryPage /></ProtectedRoute>} />
          <Route path="/report" element={<ProtectedRoute allow={["admin", "teacher"]}><ReportPage /></ProtectedRoute>} />
          <Route path="/point" element={<ProtectedRoute allow={["admin", "teacher"]}><PointPage /></ProtectedRoute>} />
          <Route path="/history/points" element={<ProtectedRoute allow={["admin", "teacher"]}><PointsHistoryPage /></ProtectedRoute>} />
          <Route path="/point/settings" element={<ProtectedRoute allow={["admin", "teacher"]}><PointSettingsPage /></ProtectedRoute>} />
          <Route path="/point/item/new" element={<ProtectedRoute allow={["admin", "teacher"]}><PointItemCardPage /></ProtectedRoute>} />
          <Route path="/point/item/:id" element={<ProtectedRoute allow={["admin", "teacher"]}><PointItemCardPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute allow={["admin"]}><BasicSettingsPage /></ProtectedRoute>} />
          <Route path="/settings/alimtalk-templates" element={<ProtectedRoute allow={["admin", "teacher"]}><AlimtalkTemplatePage /></ProtectedRoute>} />
          <Route path="/settings/permissions" element={<ProtectedRoute allow={["admin"]}><PermissionsManagementPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute allow={["admin", "teacher", "viewer"]}><ProfilePage /></ProtectedRoute>} />
          <Route path="*" element={<ProtectedRoute allow={["admin", "teacher", "viewer"]}><RedirectByRole /></ProtectedRoute>} />
                </Routes>
              </main>
            </div>
          </div>
        } />
      </Routes>
    </>
  );
}

function RedirectByRole() {
  const token = localStorage.getItem("jwtToken");
  if (!token) return <Navigate to="/login" replace />;
  const decoded = decodeJWT(token);
  if (!decoded) return <Navigate to="/login" replace />;
  if (decoded.role === "admin" || decoded.role === "teacher") return <Navigate to="/student" replace />;
  if (decoded.role === "viewer") return <Navigate to="/viewer" replace />;
  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
